// src/pages/rapports/RapportsDashboard.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { API_URL } from '../../Config';

export default function RapportsDashboard() {
  const [stats, setStats] = useState(null);
  const [parStatut, setParStatut] = useState([]);
  const [topMateriels, setTopMateriels] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [dateDebut, setDateDebut] = useState(format(new Date().setDate(new Date().getDate() - 30), 'yyyy-MM-dd'));
  const [dateFin, setDateFin] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, ps, tm, mv] = await Promise.all([
        axios.get(`${API_URL}/rapports/statistiques`, { headers }),
        axios.get(`${API_URL}/rapports/demandes-par-statut`, { headers }),
        axios.get(`${API_URL}/rapports/top-materiels?limit=10`, { headers }),
        axios.post(`${API_URL}/rapports/mouvement-stock`, { 
          date_debut: dateDebut, 
          date_fin: dateFin 
        }, { headers })
      ]);

      setStats(s.data.data);
      setParStatut(ps.data.data);
      setTopMateriels(tm.data.data);
      setMouvements(mv.data.data);
    } catch (err) {
      console.error('Erreur de chargement des rapports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refreshMouvements = () => {
    loadData();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatDateDisplay = (dateStr) => {
    return format(new Date(dateStr), 'dd MMM yyyy', { locale: fr });
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Chargement des rapports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rapports et Statistiques</h1>
              <p className="text-gray-600 mt-1">Analyse complète de l'activité et des performances</p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="text-sm text-gray-500">
                Période : {formatDateDisplay(dateDebut)} - {formatDateDisplay(dateFin)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('demandes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'demandes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Demandes
            </button>
            <button
              onClick={() => setActiveTab('materiels')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'materiels'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Matériels
            </button>
            <button
              onClick={() => setActiveTab('stock')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stock'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Mouvements de stock
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Key Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats && [
                { label: 'Total Demandes', value: stats.total_demandes, color: 'bg-blue-50', textColor: 'text-blue-700', icon: '📊' },
                { label: 'Demandes Validées', value: stats.demandes_validees, color: 'bg-green-50', textColor: 'text-green-700', icon: '✅' },
                { label: 'Demandes en Attente', value: stats.demandes_en_attente, color: 'bg-yellow-50', textColor: 'text-yellow-700', icon: '⏳' },
                { label: 'Matériels en Stock', value: stats.total_materiels, color: 'bg-purple-50', textColor: 'text-purple-700', icon: '📦' },
              ].map((stat, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{formatNumber(stat.value)}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${stat.color}`}>
                      <span className="text-2xl">{stat.icon}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Status Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Répartition des Demandes par Statut</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {parStatut.map((item, index) => {
                  const colors = {
                    en_attente: 'bg-yellow-100 text-yellow-800',
                    validee: 'bg-green-100 text-green-800',
                    refusee: 'bg-red-100 text-red-800',
                    pret_recuperation: 'bg-blue-100 text-blue-800',
                    completement_recupere: 'bg-gray-100 text-gray-800',
                  };
                  
                  return (
                    <div key={index} className="text-center p-4 border border-gray-100 rounded-lg hover: transition-colors">
                      <div className="text-2xl font-bold text-gray-900">{formatNumber(item.count)}</div>
                      <div className={`mt-2 text-xs font-medium px-2 py-1 rounded-full inline-block ${colors[item.status] || 'bg-gray-100 text-gray-800'}`}>
                        {item.status.replace(/_/g, ' ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Demandes Tab */}
        {activeTab === 'demandes' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Détails des Demandes</h2>
                <div className="text-sm text-gray-500">
                  {stats?.total_demandes} demandes au total
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4  rounded-lg">
                    <span className="text-gray-700">Demandes Validées</span>
                    <span className="font-semibold text-green-700">{formatNumber(stats?.demandes_validees || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-4  rounded-lg">
                    <span className="text-gray-700">Demandes Refusées</span>
                    <span className="font-semibold text-red-700">{formatNumber(stats?.demandes_refusees || 0)}</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4  rounded-lg">
                    <span className="text-gray-700">En Attente de Validation</span>
                    <span className="font-semibold text-yellow-700">{formatNumber(stats?.demandes_en_attente || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-4  rounded-lg">
                    <span className="text-gray-700">Matériels Faible Stock</span>
                    <span className="font-semibold text-orange-700">{formatNumber(stats?.materiels_stock_faible || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Matériels Tab */}
        {activeTab === 'materiels' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Top 10 Matériels les plus Demandés</h2>
                <p className="text-sm text-gray-600 mt-1">Classement basé sur les quantités demandées</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matériel</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Demandé</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validé</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topMateriels.map((materiel, index) => (
                      <tr key={materiel.id} className="hover: transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                              index < 3 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            } font-bold`}>
                              {index + 1}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{materiel.nom}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {materiel.reference || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {formatNumber(materiel.total_demande)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {formatNumber(materiel.total_valide)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Stock Movements Tab */}
        {activeTab === 'stock' && (
          <div className="space-y-8">
            {/* Date Range Filter */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Filtrer par période</h2>
                  <p className="text-sm text-gray-600">Sélectionnez une période pour analyser les mouvements de stock</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                    <input
                      type="date"
                      value={dateDebut}
                      onChange={e => setDateDebut(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                    <input
                      type="date"
                      value={dateFin}
                      onChange={e => setDateFin(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={refreshMouvements}
                      className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Actualiser
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Movements Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Mouvements de Stock</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Période : {formatDateDisplay(dateDebut)} - {formatDateDisplay(dateFin)}
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matériel</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sorties</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retours</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mouvement Net</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mouvements.length > 0 ? (
                      mouvements.map((mouvement, index) => {
                        const net = (mouvement.retours || 0) - (mouvement.sorties || 0);
                        return (
                          <tr key={index} className="hover: transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{mouvement.nom}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {mouvement.reference || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                                <span className="text-sm font-medium text-gray-900">
                                  {formatNumber(mouvement.sorties || 0)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                                <span className="text-sm font-medium text-gray-900">
                                  {formatNumber(mouvement.retours || 0)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                net >= 0 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {net > 0 ? '+' : ''}{formatNumber(net)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="text-gray-500">
                            Aucun mouvement de stock pour la période sélectionnée
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-t border-gray-200">
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Synthèse</h3>
              <p className="text-sm text-gray-600 mt-1">
                Données mises à jour le {format(new Date(), "dd MMMM yyyy 'à' HH:mm", { locale: fr })}
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <button
                onClick={loadData}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover: focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Actualiser tous les rapports
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}